import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button, showErrorToast, showSuccessToast } from '@/components';
import {
  ApiKeyEvent,
  ApiKeyEnvironment,
  ApiKeyRow,
  listApiKeyEvents,
  listApiKeys,
  revokeApiKey,
} from '@/services/api/apiKeys';
import { extractApiErrorMessage } from '@/utils/errorHandler';
import { useIntegrationAccess } from '@/contexts/IntegrationAccessContext';
import ApiDocsTab from './ApiDocsTab';
import CreateApiKeyModal from './CreateApiKeyModal';
import RevokeApiKeyModal from './RevokeApiKeyModal';
import SandboxSimulatorTab from './SandboxSimulatorTab';
import OrderWebhooksSection from './OrderWebhooksSection';
import WebhooksSection from './WebhooksSection';

type DeveloperTab = 'keys' | 'docs' | 'sandbox' | 'webhooks';

const TABS: { key: DeveloperTab; label: string; icon: string }[] = [
  { key: 'keys', label: 'API keys', icon: 'ri-key-2-line' },
  { key: 'docs', label: 'API docs', icon: 'ri-book-2-line' },
  { key: 'sandbox', label: 'Sandbox', icon: 'ri-flask-line' },
  { key: 'webhooks', label: 'Webhooks', icon: 'ri-broadcast-line' },
];

const formatTime = (value: string | null) =>
  value ? dayjs(value).format('DD MMM YYYY, HH:mm') : '—';

// ─── Environment presentation ─────────────────────────────────────────
// Production (LIVE, dash_live_…) and Sandbox (TEST, dash_test_…) are hard
// walls: a sandbox key can never authenticate production traffic and vice
// versa. Everything on this page is grouped by that boundary so a developer
// always knows which side of the wall a credential belongs to.
export const ENV_META: Record<
  ApiKeyEnvironment,
  { label: string; badge: string; dot: string }
> = {
  LIVE: {
    label: 'Production',
    badge: 'bg-success-lighter text-success-dark',
    dot: 'bg-success-base',
  },
  TEST: {
    label: 'Sandbox',
    badge: 'bg-warning-lighter text-warning-dark',
    dot: 'bg-warning-base',
  },
};

export const EnvBadge: React.FC<{ environment: ApiKeyEnvironment }> = ({
  environment,
}) => {
  const meta = ENV_META[environment];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.badge}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
};

const EVENT_STYLES: Record<string, string> = {
  CREATED: 'bg-success-lighter text-success-dark',
  REVOKED: 'bg-error-lighter text-error-dark',
  EXPIRED: 'bg-bg-soft-200 text-text-sub-600',
  AUTH_REJECTED: 'bg-warning-lighter text-warning-dark',
};

// ─── Key history (append-only events, fetched on expand) ─────────────
const KeyHistory: React.FC<{ keyId: string }> = ({ keyId }) => {
  const [events, setEvents] = useState<ApiKeyEvent[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listApiKeyEvents(keyId)
      .then((rows) => !cancelled && setEvents(rows))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [keyId]);

  if (error)
    return (
      <p className="py-2 text-xs text-error-base">Failed to load history.</p>
    );
  if (!events)
    return <p className="py-2 text-xs text-text-soft-400">Loading history…</p>;
  if (events.length === 0)
    return <p className="py-2 text-xs text-text-soft-400">No events yet.</p>;

  return (
    <ul className="divide-y divide-stroke-soft-200">
      {events.map((e) => (
        <li key={e.id} className="flex items-start gap-3 py-2 text-xs">
          <span
            className={`mt-px inline-block rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold ${
              EVENT_STYLES[e.event] ?? 'bg-bg-soft-200 text-text-sub-600'
            }`}
          >
            {e.event}
          </span>
          <span className="flex-1 text-text-sub-600">
            {e.note ?? '—'}
            {e.actor && (
              <span className="text-text-soft-400"> · {e.actor}</span>
            )}
          </span>
          <span className="whitespace-nowrap text-text-soft-400">
            {formatTime(e.createdAt)}
          </span>
        </li>
      ))}
    </ul>
  );
};

// ─── One key row ──────────────────────────────────────────────────────
const KeyCard: React.FC<{
  row: ApiKeyRow;
  onRevoke: (row: ApiKeyRow) => void;
}> = ({ row, onRevoke }) => {
  const [showHistory, setShowHistory] = useState(false);
  const revoked = row.revokedAt != null;

  return (
    <div
      className={`rounded-xl border border-stroke-soft-200 bg-bg-white-0 px-5 py-4 ${
        revoked ? 'opacity-70' : ''
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-text-strong-950">
          {row.name}
        </span>
        <EnvBadge environment={row.environment} />
        {revoked && (
          <span className="rounded-full bg-error-lighter px-2 py-0.5 text-[11px] font-semibold text-error-dark">
            Revoked
          </span>
        )}
        {row.scopes.map((s) => (
          <span
            key={s}
            className="rounded-full bg-bg-weak-50 px-2 py-0.5 font-mono text-[11px] text-text-sub-600"
          >
            {s}
          </span>
        ))}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-sub-600">
        <span className="font-mono text-text-strong-950">
          {row.keyPrefix}
          ••••••••
        </span>
        <span>Last used: {formatTime(row.lastUsedAt)}</span>
        <span>Created: {formatTime(row.createdAt)}</span>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button
          variant="white"
          size="small"
          onClick={() => setShowHistory((v) => !v)}
        >
          <i className="ri-history-line mr-1.5" />
          {showHistory ? 'Hide history' : 'History'}
        </Button>
        {!revoked && (
          <Button variant="white" size="small" onClick={() => onRevoke(row)}>
            <i className="ri-forbid-line mr-1.5 text-error-base" />
            <span className="text-error-base">Revoke</span>
          </Button>
        )}
      </div>

      {showHistory && (
        <div className="mt-3 rounded-lg bg-bg-weak-50 px-4 py-1">
          <KeyHistory keyId={row.id} />
        </div>
      )}
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────
const DeveloperPage: React.FC = () => {
  // The tab lives in the URL (`?tab=`) so a refresh — or a shared Sandbox
  // shipment link (`?tab=sandbox&shipment=<id>`) — lands where it was.
  const [params, setParams] = useSearchParams();
  const tab: DeveloperTab = TABS.some((t) => t.key === params.get('tab'))
    ? (params.get('tab') as DeveloperTab)
    : 'keys';
  const setTab = (next: DeveloperTab) => setParams({ tab: next });
  // Which APIs this client may see: shipments and/or WMS outbound orders,
  // from the backend (sandbox-availability). The route gate guarantees at
  // least one and that the answer is in.
  const { access } = useIntegrationAccess();
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyRow | null>(null);

  const load = useCallback(async () => {
    try {
      setKeys(await listApiKeys());
    } catch (err) {
      showErrorToast(
        'Failed to load API keys',
        extractApiErrorMessage(err, 'Failed to load API keys')
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevoke = async (row: ApiKeyRow) => {
    try {
      await revokeApiKey(row.id);
      showSuccessToast(
        'API key revoked',
        `${row.keyPrefix}… stops working on its next request`
      );
      setRevokeTarget(null);
      load();
    } catch (err) {
      showErrorToast(
        'Failed to revoke key',
        extractApiErrorMessage(err, 'Failed to revoke key')
      );
    }
  };

  const byEnv = (env: ApiKeyEnvironment) =>
    keys.filter((k) => k.environment === env);

  return (
    // Full width like the Shipments page (`p-4 sm:p-6`, no max width): one
    // width for every tab so switching tabs never shifts the layout, and the
    // docs tab's side-by-side prose/code columns grow with the screen.
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-text-strong-950">
          Developer
        </h1>
        <p className="mt-1 text-sm text-text-sub-600">
          Credentials, docs, and a sandbox for integrating your systems with the
          Dash API:{' '}
          {[
            access.shipments && 'shipments',
            access.orders && 'warehouse outbound orders',
          ]
            .filter(Boolean)
            .join(' and ')}
          .
        </p>
      </div>

      {/* Tabs */}
      <div
        role="tablist"
        className="flex gap-1 border-b border-stroke-soft-200"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors ${
              tab === t.key
                ? 'border-primary text-primary'
                : 'border-transparent text-text-sub-600 hover:text-text-strong-950'
            }`}
          >
            <i className={`${t.icon} mr-1.5`} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'docs' && <ApiDocsTab access={access} />}
      {tab === 'sandbox' && (
        <SandboxSimulatorTab access={access} onNavigate={setTab} />
      )}
      {tab === 'webhooks' && (
        <div className="space-y-6">
          {access.shipments && <WebhooksSection />}
          {access.orders && <OrderWebhooksSection />}
        </div>
      )}
      {tab !== 'keys' ? null : (
        <>
          {/* ── API keys ───────────────────────────────────────────── */}
          <section className="rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-text-strong-950">
                  API keys
                </h2>
                <p className="mt-0.5 max-w-xl text-xs text-text-sub-600">
                  Send a key in the <code className="font-mono">x-api-key</code>{' '}
                  header. Keys are shown once at creation — we store only a
                  hash, so a lost key must be replaced, never recovered.
                </p>
              </div>
              <Button onClick={() => setCreateOpen(true)}>
                <i className="ri-add-line mr-1.5" />
                Create API key
              </Button>
            </div>

            {/* Environment wall — the one rule developers must internalize. */}
            <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-information-lighter px-4 py-3 text-xs text-information-dark">
              <i className="ri-shield-keyhole-line mt-px text-sm" />
              <span>
                <b>Production and Sandbox are separate environments.</b> A
                Sandbox key (<code className="font-mono">dash_test_…</code>)
                only works for sandbox requests; a Production key (
                <code className="font-mono">dash_live_…</code>) only for
                production. Going live means swapping the key — same URL, same
                payload.
              </span>
            </div>

            {loading ? (
              <p className="mt-6 text-sm text-text-soft-400">Loading keys…</p>
            ) : keys.length === 0 ? (
              <div className="mt-6 rounded-xl border border-dashed border-stroke-sub-300 px-6 py-10 text-center">
                <i className="ri-key-2-line text-2xl text-text-soft-400" />
                <p className="mt-2 text-sm font-medium text-text-strong-950">
                  No API keys yet
                </p>
                <p className="mt-1 text-xs text-text-sub-600">
                  Create a Sandbox key first to build your integration, then a
                  Production key to go live.
                </p>
              </div>
            ) : (
              <div className="mt-6 space-y-6">
                {(['LIVE', 'TEST'] as const).map((env) => {
                  const rows = byEnv(env);
                  if (rows.length === 0) return null;
                  return (
                    <div key={env}>
                      <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-soft-400">
                        <EnvBadge environment={env} />
                      </h3>
                      <div className="space-y-3">
                        {rows.map((row) => (
                          <KeyCard
                            key={row.id}
                            row={row}
                            onRevoke={setRevokeTarget}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      <CreateApiKeyModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
        access={access}
      />
      <RevokeApiKeyModal
        row={revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevoke}
      />
    </div>
  );
};

export default DeveloperPage;
